// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Reading the player's hands out of a WebXR frame (M5-02, M5-03).
//
// The thin part. Everything that *decides* anything lives elsewhere — this walks
// `session.inputSources`, asks the frame where things are, and fills two `HandReport`s. It is the
// only file in the product that knows the shape of an `XRInputSource`.
//
// **A hand and a controller are the same report.** The rest of the XR code asks "where is the
// left hand's contact point and is it pinching", and gets an answer whether the player is wearing
// tracked hands or holding a Touch controller. Two parallel input paths through the cabinet code
// would be two places to fix every bug, and the difference between them is genuinely only three
// lines: which space the position comes from, what counts as a pinch, and whether there is a
// trigger.
//
// **The contact point is the index fingertip, not the wrist.** For hands that is literal. For
// controllers it is the *grip* space — the pose of the handle in your fist — because that is what
// a player pushes a cabinet with, and because the target-ray space points off into the distance
// from somewhere near the front of the controller and would put the contact point inside the
// machine.
//
// **Absence is a state, not an error.** A hand that is not tracked this frame reports
// `present: false` and has its pose reset, which is what stops a hand that vanishes at one side
// of the play space and reappears at the other from differencing into a slam tilt.

import { emptyPose, resetPose, trackPose, type TrackedPose } from './poseTracking'
import type { HapticActuatorPort } from './haptics'
import type { InputMethod, TrackedSource } from './inputMethod'

/** Any of the position-shaped things WebXR returns. */
export interface Vec3Like {
  readonly x: number
  readonly y: number
  readonly z: number
}

/** Any of the quaternion-shaped things WebXR returns. */
export interface QuatLike {
  readonly x: number
  readonly y: number
  readonly z: number
  readonly w: number
}

export interface XRPosePort {
  /**
   * The orientation is optional because the fakes in the tests, and the joint reader here, only
   * ever needed a position; the one reader of it is the anchor restore (`XRRuntime`), which reads
   * the machine's yaw off it and falls back to the record's without it (14 Sep 2026).
   */
  readonly transform: { readonly position: Vec3Like; readonly orientation?: QuatLike | undefined }
  /** A joint pose's radius, metres — `XRJointPose.radius`. Absent on every other pose. */
  readonly radius?: number | undefined
}

/** The slice of an `XRFrame` this product uses. */
export interface XRFramePort {
  getPose: (space: unknown, base: unknown) => XRPosePort | undefined | null
  getJointPose?: (joint: unknown, base: unknown) => XRPosePort | undefined | null
}

/** The slice of an `XRInputSource` this product uses. */
export interface XRInputSourcePort {
  readonly handedness: string
  /** Present only for tracked hands. A `Map`-alike keyed by joint name. */
  readonly hand?: { get: (joint: string) => unknown } | undefined | null
  readonly gripSpace?: unknown
  readonly targetRaySpace?: unknown
  /** `tracked-pointer` for a controller or hand, `transient-pointer` for a system gesture, `gaze`, `screen`. */
  readonly targetRayMode?: string | undefined
  /**
   * **Nullable, not merely optional.** The DOM attribute is `Gamepad?` — `null` on every input
   * source that has no gamepad: a tracked hand, a transient pointer the system raises for one of
   * its own overlays. A guard that tests only `undefined` lets `null` through to `.buttons`, and on
   * the headset that is one exception in the frame callback, after which nothing is drawn again
   * (the picture went black on the first frame of a session that had the keyboard overlay up).
   */
  readonly gamepad?:
    | {
        readonly buttons: readonly { readonly pressed: boolean; readonly value: number }[]
        /**
         * The `xr-standard` axes, for comfort mode's thumbstick nudge (XR-007).
         *
         * Indices 2 and 3 are the thumbstick; 0 and 1 are a touchpad most controllers do not have.
         * Optional because a tracked hand has no gamepad at all and an emulator may report a
         * gamepad with no axes — both of which are "no stick", not "a stick at centre".
         */
        readonly axes?: readonly number[] | undefined
        readonly hapticActuators?: readonly HapticActuatorPort[]
      }
    | undefined
    | null
}

/** What one hand is doing this frame, whatever it is made of. */
export interface HandReport {
  readonly handedness: 'left' | 'right'
  /** Is the runtime giving us a pose for it? */
  present: boolean
  kind: InputMethod
  /** The contact point: index fingertip, or the controller's grip. */
  readonly tip: TrackedPose
  /**
   * The point a pinch is made *with*: midway between the thumb tip and the index tip, or the
   * controller's grip. **Not the fingertip**, for a carry: closing a pinch moves the index tip two
   * to four centimetres toward the thumb, so a thing tested against the fingertip on the frame the
   * pinch closes is tested somewhere the author did not aim — the second device day of M13 read
   * `over slingRight (a)/(b)` nine times in three seconds before the sling was taken. The midpoint
   * barely moves as the fingers meet; it is the Quest's own pinch position. Falls back to the
   * fingertip while the thumb is untracked, so a carry never loses its point mid-air.
   */
  readonly pinch: TrackedPose
  /** The palm, for the wrist-menu gesture and for shoving with a flat hand. */
  readonly palm: TrackedPose
  /** Pinching (hands) or squeezing the grip (controllers). The grab gesture. */
  grab: boolean
  /**
   * Whether anything actually measured the grab this frame.
   *
   * **A pinch nobody could measure is not a pinch.** `grab` is deliberately sticky — the
   * hysteresis below needs last frame's answer — but the thumb-tip joint drops out of tracking
   * constantly, and when it does the pinch test never runs. Without this flag a hand whose thumb
   * vanished while closed stays closed *forever*, after which merely bringing it near the cabinet
   * carries the machine off. Measured on the headset first; the same defect is in both clients
   * because both clients read the same joint the same way.
   */
  grabMeasured: boolean
  /**
   * Where `grab` came from this frame (13 Sep 2026): `runtime` is the session's own
   * `selectstart`/`selectend` for this hand — the pinch the headset detects with its hand model and
   * uses for its whole interface — `distance` the thumb-to-index rule below, `button` a controller's
   * squeeze, `none` an absent hand. The `[xr] hands` line prints it beside the millimetres.
   */
  grabSource: 'runtime' | 'distance' | 'button' | 'none'
  /**
   * The thumb tip on the index tip — a pinch by the ruler, whatever the runtime says (15 Sep
   * 2026). The 01:37 tap: `R hands 5mm/evt`, thumb and index five millimetres apart at the
   * plunger, and `grab` false, because the hand had been named by the runtime and the runtime
   * fires `select` for a pinch made out in front, not for one made at waist height round a knob
   * — "plunger pull is not consistent, many times pulled while ball highlighted and didn't move
   * plunger". Closes at `PINCH_TIGHT`, where a relaxed hand cannot be (the same as `PINCH_CLOSE`
   * since 1.0.235), opens at `PINCH_OPEN`. The plunger reads it beside `grab`; the wrist panel does not,
   * because a pointing hand is the one pose whose tips touch without a pinch being meant.
   */
  pinchTight: boolean
  /** Thumb tip to index tip, metres, as last measured for a tracked hand. For the console. */
  lastPinchMetres: number

  /**
   * Is the hand actually *closed* — fingers curled toward the palm — rather than pinched?
   *
   * **`cabinetGrab`'s header has said since M7-13 that "an open hand shoves the machine, a closed
   * one carries it", and the only test for a closed hand was `grab`, which is a pinch.** A player
   * pressing a flipper button, or resting two fingers together, reads as pinching. A Quest 3
   * measured it on 1 Sep 2026: across eighteen heartbeats of ordinary play a hand read closed in
   * five of them, and one of those five became a real carry. Reported as "grip on corner to move
   * pinball machine fires when not gripped". The two clients share this file's shape, so the
   * browser had it too.
   *
   * A pinch is still exactly right for the plunger, so `grab` keeps its meaning and this is a
   * second, narrower question.
   */
  closed: boolean

  /**
   * True when something actually measured `closed` this frame.
   *
   * Consumed the *opposite* way to `grabMeasured`: an unmeasured pinch becomes "not grabbing",
   * while an unmeasured fist means "this source cannot say" — and a caller that read that as "open"
   * would silently stop every controller and every test that fills a hand by hand.
   */
  closedMeasured: boolean

  /** The last mean fingertip-to-palm distance measured, metres. Diagnostic only. */
  lastFistMetres: number
  /** The trigger, controllers only. Always false for hands. */
  trigger: boolean
  /** This source's haptic actuator, if it has one. */
  actuator: HapticActuatorPort | undefined
}

function emptyHand(handedness: 'left' | 'right'): HandReport {
  return {
    handedness,
    present: false,
    kind: 'controllers',
    tip: emptyPose(),
    pinch: emptyPose(),
    palm: emptyPose(),
    grab: false,
    grabMeasured: false,
    grabSource: 'none',
    pinchTight: false,
    lastPinchMetres: 0,
    closed: false,
    closedMeasured: false,
    lastFistMetres: 0,
    trigger: false,
    actuator: undefined,
  }
}

/**
 * Pinch thresholds, metres between thumb tip and index tip.
 *
 * Hysteresis again, and for the same reason the flipper buttons have it: a pinch held at a single
 * threshold flickers, and a flickering pinch drops the plunger mid-pull. 25 mm to close and 40 mm
 * to open — a deliberate pinch closes to under 15 mm, and a relaxed hand sits well over 60.
 *
 * **The fallback, not the rule, since 13 Sep 2026.** "A relaxed hand sits well over 60" was not
 * true of the author's pointing hand on the device: the thumb rides the side of the index finger
 * at thirty to forty millimetres from its tip, so once a pinch had closed the hand never read open
 * again and the next real pinch was no edge — `[xr] hands` printed `R hands pinch` on eighty
 * samples in ninety seconds while parts were hovered and not taken ("sometimes moved, sometimes
 * not"). The session's own `selectstart`/`selectend` is the headset's pinch, detected with its hand
 * model and used for its whole interface; when the runtime has said so for a hand, that is the
 * grab, and these two numbers decide only when it has not.
 *
 * **15 mm to close and 30 mm to open since 1.0.235**, with the Quest's `HandReport` (which has
 * no runtime pinch, so the ruler is all it has). The owner's headset logs of 24-25 Oct 2026: a
 * part held in Build mode measured 3-12 mm in 242 of 244 samples, while a hand curled at the
 * flipper buttons or resting sat at 15-40 mm, mostly 20-29 — inside the old band, so a resting
 * hand that once dipped under 25 mm read pinched until it opened past 40.
 */
export const PINCH_CLOSE = 0.015
export const PINCH_OPEN = 0.030
/** `HandReport.pinchTight` closes here: fingertips touching, a hand no relaxed pose reaches. */
export const PINCH_TIGHT = 0.015

/**
 * Fist thresholds: the mean distance from the tracked fingertips to the palm, metres.
 *
 * **Judgement, not measurement, and the instrument that will replace them ships alongside.** An
 * open hand puts its fingertips something like 0.11 m from the palm and a hand wrapped around a
 * corner post perhaps 0.05 to 0.07 m, so 0.080 to close and 0.105 to open sit in the gap with
 * hysteresis across it. The Quest's heartbeat prints the measured mean every five seconds; these
 * two numbers are expected to be replaced by what it says.
 *
 * The mean rather than the largest, because a runtime that drops a little-finger joint would
 * otherwise veto a real grip.
 */
export const FIST_CLOSE = 0.080

export const FIST_OPEN = 0.105

/** The fingertips beyond the index, for the fist measurement. The index is read separately. */
const FIST_JOINTS = ['middle-finger-tip', 'ring-finger-tip', 'pinky-finger-tip'] as const

/** Standard `xr-standard` gamepad mapping: 0 is the trigger, 1 is the squeeze. */
const TRIGGER_BUTTON = 0
const SQUEEZE_BUTTON = 1

/** A button is down past this fraction of its travel. */
const BUTTON_THRESHOLD = 0.55

/**
 * Both hands, refreshed every frame.
 *
 * One instance for the whole session. The reports are mutated in place rather than rebuilt: two
 * objects a frame for the length of a session is exactly the steady garbage that shows up as a
 * periodic hitch on a mobile GPU, and this is the hottest per-frame path in the XR client.
 */
export class HandTracker {
  readonly left = emptyHand('left')
  readonly right = emptyHand('right')

  /** Reused so `sources()` allocates nothing. */
  private readonly summary: TrackedSource[] = [
    { kind: 'controllers', tracked: false },
    { kind: 'controllers', tracked: false },
  ]

  /**
   * Read one frame.
   *
   * `referenceSpace` is whatever the session is presenting in — `local-floor` here — so every
   * position that comes out is in that space. Converting to cabinet space is the rig's job,
   * because only the rig knows where the cabinet was placed.
   */
  advance(
    frame: XRFramePort,
    referenceSpace: unknown,
    sources: Iterable<XRInputSourcePort>,
    frameSeconds: number,
    /** What the session's own select events say per hand; `undefined` for a hand they never named. */
    pinch?: { readonly left?: boolean; readonly right?: boolean },
  ): void {
    this.left.present = false
    this.right.present = false
    this.left.grabMeasured = false
    this.right.grabMeasured = false

    for (const source of sources) {
      const hand = source.handedness === 'left' ? this.left : source.handedness === 'right' ? this.right : undefined
      if (hand === undefined) continue
      this.readOne(hand, frame, referenceSpace, source, frameSeconds, pinch?.[hand.handedness])
    }

    // A hand the runtime stopped reporting has to forget where it was, or the next sighting
    // differences into a velocity nobody produced.
    if (!this.left.present) this.clear(this.left)
    if (!this.right.present) this.clear(this.right)

    // A hand that *is* being tracked but whose pinch nobody could measure reads as open. Better
    // to lose a real grab for the one frame the thumb joint is missing than to carry a stale
    // closed hand for the rest of the session — the first costs a frame of plunger tension, the
    // second picks the machine up when the player reaches past it.
    if (this.left.present && !this.left.grabMeasured) this.left.grab = false
    if (this.right.present && !this.right.grabMeasured) this.right.grab = false
    if (this.left.present && !this.left.grabMeasured) this.left.pinchTight = false
    if (this.right.present && !this.right.grabMeasured) this.right.pinchTight = false
  }

  /** What the input-method tracker needs: which kinds are being tracked right now. */
  trackedSources(): readonly TrackedSource[] {
    this.summary[0] = { kind: this.left.kind, tracked: this.left.present }
    this.summary[1] = { kind: this.right.kind, tracked: this.right.present }
    return this.summary
  }

  private clear(hand: HandReport): void {
    resetPose(hand.tip)
    resetPose(hand.pinch)
    resetPose(hand.palm)
    hand.grab = false
    hand.grabMeasured = false
    hand.grabSource = 'none'
    hand.pinchTight = false
    // The fist too (13 Sep 2026): `[xr] hands` read `R absent fist` — a hand that vanished closed
    // stayed closed, and the hysteresis above then needed the wider open threshold before the
    // returning hand read as open, so a relaxed hand came back as a carry for a while.
    hand.closed = false
    hand.closedMeasured = false
    hand.trigger = false
    hand.actuator = undefined
  }

  private readOne(
    hand: HandReport,
    frame: XRFramePort,
    space: unknown,
    source: XRInputSourcePort,
    frameSeconds: number,
    told?: boolean,
  ): void {
    const joints = source.hand
    if (joints !== undefined && joints !== null && frame.getJointPose !== undefined) {
      const tip = frame.getJointPose(joints.get('index-finger-tip'), space)
      const thumb = frame.getJointPose(joints.get('thumb-tip'), space)
      const palm = frame.getJointPose(joints.get('middle-finger-metacarpal'), space)
      // **The other three fingertips, so a fist can be told from a pinch** (M8-45). The Quest's
      // reader has sampled them since M8-28 for the cabinet buttons; this one never needed them
      // until "closed hand" had to mean something other than "pinching".
      const others = FIST_JOINTS.map((name) => frame.getJointPose?.(joints.get(name), space))
      if (tip === undefined || tip === null) return
      hand.kind = 'hands'
      hand.present = true
      hand.actuator = undefined
      hand.trigger = false
      const p = tip.transform.position
      trackPose(hand.tip, p.x, p.y, p.z, frameSeconds)
      if (palm !== undefined && palm !== null) {
        const q = palm.transform.position
        trackPose(hand.palm, q.x, q.y, q.z, frameSeconds)
      }
      if (thumb !== undefined && thumb !== null) {
        const t = thumb.transform.position
        const d = Math.hypot(p.x - t.x, p.y - t.y, p.z - t.z)
        hand.lastPinchMetres = d
        if (told !== undefined) {
          hand.grab = told
          hand.grabSource = 'runtime'
        } else {
          hand.grab = hand.grab ? d < PINCH_OPEN : d < PINCH_CLOSE
          hand.grabSource = 'distance'
        }
        // The ruler's own tight answer, kept whatever the runtime said (15 Sep 2026).
        hand.pinchTight = hand.pinchTight ? d < PINCH_OPEN : d < PINCH_TIGHT
        hand.grabMeasured = true
        trackPose(hand.pinch, (p.x + t.x) / 2, (p.y + t.y) / 2, (p.z + t.z) / 2, frameSeconds)
      } else if (told !== undefined) {
        // No thumb this frame, but the runtime has an answer: it measured what the frame did not.
        hand.grab = told
        hand.grabSource = 'runtime'
        hand.pinchTight = false
        hand.grabMeasured = true
        trackPose(hand.pinch, p.x, p.y, p.z, frameSeconds)
      } else {
        trackPose(hand.pinch, p.x, p.y, p.z, frameSeconds)
      }
      // The fist needs the palm; without it the question is unanswerable and saying so is the
      // point — see `closedMeasured`.
      if (palm !== undefined && palm !== null) {
        const q = palm.transform.position
        let reach = Math.hypot(p.x - q.x, p.y - q.y, p.z - q.z)
        let counted = 1
        for (const other of others) {
          if (other === undefined || other === null) continue
          const o = other.transform.position
          reach += Math.hypot(o.x - q.x, o.y - q.y, o.z - q.z)
          counted += 1
        }
        const mean = reach / counted
        hand.closed = hand.closed ? mean < FIST_OPEN : mean < FIST_CLOSE
        hand.closedMeasured = true
        hand.lastFistMetres = mean
      }
      return
    }

    // A controller. The grip space, not the target ray — see the note at the top.
    const grip = source.gripSpace ?? source.targetRaySpace
    const pose = grip === undefined || grip === null ? undefined : frame.getPose(grip, space)
    if (pose === undefined || pose === null) return
    hand.kind = 'controllers'
    hand.present = true
    const p = pose.transform.position
    trackPose(hand.tip, p.x, p.y, p.z, frameSeconds)
    trackPose(hand.pinch, p.x, p.y, p.z, frameSeconds)
    trackPose(hand.palm, p.x, p.y, p.z, frameSeconds)

    const pad = source.gamepad
    hand.trigger = buttonDown(pad, TRIGGER_BUTTON)
    hand.grab = buttonDown(pad, SQUEEZE_BUTTON)
    hand.grabSource = 'button'
    // A controller's squeeze button is always readable, so it is always measured.
    hand.grabMeasured = true
    // **A squeezed grip is a closed hand** (M8-45). There are no fingers to measure and the button
    // means what the fist means on the other input method, so it is stated rather than left unknown.
    hand.closed = hand.grab
    hand.closedMeasured = true
    hand.actuator = pad?.hapticActuators?.[0]
  }
}

function buttonDown(
  pad: XRInputSourcePort['gamepad'],
  index: number,
): boolean {
  const button = pad?.buttons[index]
  if (button === undefined) return false
  return button.pressed || button.value >= BUTTON_THRESHOLD
}
